<template>
  <v-navigation-drawer v-model="drawer" right temporary width="380" class="group-info-sidebar"
    :class="{ 'theme--dark': $vuetify.theme.dark }" style="z-index: 1000; position: fixed; top: 0; height: 100vh;" app>
    <!-- Close button -->
    <div class="drawer-header">
      <v-btn icon @click="closeDrawer" class="close-btn">
        <v-icon>mdi-close</v-icon>
      </v-btn>
      <span class="drawer-title">Group Info</span>
    </div>

    <div v-if="loading" class="loading-state">
      <v-progress-circular indeterminate color="primary" size="40" />
      <span class="loading-text">Loading group info...</span>
    </div>

    <div v-else-if="groupInfo" class="drawer-content">
      <!-- Group Avatar & Name -->
      <div class="group-header">
        <div class="group-avatar-wrapper">
          <div class="avatar-container" @mouseenter="showAvatarEdit = true" @mouseleave="showAvatarEdit = false">
            <editable-avatar :image="groupInfo.groupImage || require('@/assets/default.jpg')" :size="100"
              :editable="false" class="group-avatar-large" />
            <div v-if="isAdminOrCreator" class="avatar-edit-overlay" v-show="showAvatarEdit"
              @click="triggerImageUpload">
              <v-icon size="28" color="white">mdi-camera</v-icon>
              <span class="edit-text">Change Photo</span>
            </div>
          </div>
          <input type="file" ref="imageInput" accept="image/*" style="display: none" @change="handleImageUpload" />
        </div>

        <!-- Group Name -->
        <div class="group-name-wrapper">
          <div v-if="!editingName" class="name-display">
            <h2 class="group-name">{{ groupInfo.name }}</h2>
            <v-btn v-if="isAdminOrCreator" icon small class="edit-btn" @click="startEditName">
              <v-icon size="16">mdi-pencil</v-icon>
            </v-btn>
          </div>
          <div v-else class="name-edit">
            <v-text-field v-model="editName" label="Group Name" outlined dense class="edit-name-input"
              @keyup.enter="saveName" @keyup.esc="cancelEditName" ref="nameInput" :loading="saving" />
            <div class="edit-actions">
              <v-btn small text @click="cancelEditName" :disabled="saving">Cancel</v-btn>
              <v-btn small color="primary" @click="saveName" :loading="saving">
                <v-icon left size="16">mdi-check</v-icon>
                Save
              </v-btn>
            </div>
          </div>
        </div>

        <!-- Group Description -->
        <div class="group-description-wrapper">
          <div v-if="!editingDescription" class="description-display">
            <p class="group-description">{{ groupInfo.description || 'No description' }}</p>
            <v-btn v-if="isAdminOrCreator" icon small class="edit-btn" @click="startEditDescription">
              <v-icon size="16">mdi-pencil</v-icon>
            </v-btn>
          </div>
          <div v-else class="description-edit">
            <v-text-field v-model="editDescription" label="Group Description" outlined dense
              class="edit-description-input" @keyup.enter="saveDescription" @keyup.esc="cancelEditDescription"
              ref="descriptionInput" :loading="saving" />
            <div class="edit-actions">
              <v-btn small text @click="cancelEditDescription" :disabled="saving">Cancel</v-btn>
              <v-btn small color="primary" @click="saveDescription" :loading="saving">
                <v-icon left size="16">mdi-check</v-icon>
                Save
              </v-btn>
            </div>
          </div>
        </div>

        <div class="group-stats">
          <span class="stat-item">
            <v-icon size="14" color="grey">mdi-account-group</v-icon>
            {{ participantCount }} participants
          </span>
          <span class="stat-item">
            <v-icon size="14" color="grey">mdi-clock</v-icon>
            Created {{ formatDate(groupInfo.createdAt) }}
          </span>
        </div>
      </div>

      <!-- Created By -->
      <div class="info-section">
        <h3 class="section-title">Created By</h3>
        <div class="creator-info">
          <editable-avatar :image="getUserAvatar(groupInfo.createdBy)" :size="40" :editable="false" />
          <div class="creator-details">
            <span class="creator-name">{{ getDisplayName(groupInfo.createdBy) }}</span>
            <span class="creator-email">{{ groupInfo.createdBy?.email || '' }}</span>
          </div>
        </div>
      </div>

      <!-- Media & Links Accordion -->
      <div class="info-section">
        <v-expansion-panels accordion>
          <!-- Media Files -->
          <v-expansion-panel>
            <v-expansion-panel-header class="accordion-header">
              <div class="accordion-title">
                <v-icon size="20" color="primary" class="mr-2">mdi-image-multiple</v-icon>
                <span>Media Files</span>
                <v-chip small color="primary" text-color="white" class="ml-2">
                  {{ mediaFileCount }}
                </v-chip>
              </div>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <div v-if="mediaFiles.length === 0" class="empty-state-text">
                <v-icon size="40" color="grey lighten-2">mdi-image-off</v-icon>
                <p>No media files shared yet</p>
              </div>
              <div v-else class="media-grid">
                <div v-for="(media, index) in mediaFiles" :key="index" class="media-item"
                  @click="openMediaPreview(media)">
                  <v-img :src="media.url" class="media-thumbnail" aspect-ratio="1" cover>
                    <template v-slot:placeholder>
                      <div class="media-placeholder">
                        <v-progress-circular indeterminate color="primary" size="24" />
                      </div>
                    </template>
                  </v-img>
                  <div class="media-overlay">
                    <v-icon size="24" color="white">mdi-eye</v-icon>
                  </div>
                  <div class="media-type-badge">
                    <v-icon size="14" color="white">{{ getMediaIcon(media.type) }}</v-icon>
                  </div>
                </div>
              </div>
            </v-expansion-panel-content>
          </v-expansion-panel>

          <!-- Documents -->
          <v-expansion-panel>
            <v-expansion-panel-header class="accordion-header">
              <div class="accordion-title">
                <v-icon size="20" color="orange" class="mr-2">mdi-file-document</v-icon>
                <span>Documents</span>
                <v-chip small color="orange" text-color="white" class="ml-2">
                  {{ documentCount }}
                </v-chip>
              </div>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <div v-if="documents.length === 0" class="empty-state-text">
                <v-icon size="40" color="grey lighten-2">mdi-file-document-off</v-icon>
                <p>No documents shared yet</p>
              </div>
              <div v-else class="documents-list">
                <div v-for="(doc, index) in documents" :key="index" class="document-item"
                  @click="downloadDocument(doc)">
                  <div class="document-icon">
                    <v-icon size="28" :color="getDocumentColor(doc.type)">{{ getDocumentIcon(doc.type) }}</v-icon>
                  </div>
                  <div class="document-details">
                    <div class="document-name">{{ doc.name || 'Document' }}</div>
                    <div class="document-meta">
                      <span class="document-size">{{ formatFileSize(doc.size) }}</span>
                      <span class="document-sender">{{ doc.uploadedByName || 'Unknown' }}</span>
                      <span class="document-time">{{ formatMessageTime(doc.uploadedAt) }}</span>
                    </div>
                  </div>
                  <v-btn icon small class="download-doc-btn" @click.stop="downloadDocument(doc)">
                    <v-icon size="18" color="primary">mdi-download</v-icon>
                  </v-btn>
                </div>
              </div>
            </v-expansion-panel-content>
          </v-expansion-panel>

          <!-- External Media Links (YouTube, Vimeo, etc.) -->
          <v-expansion-panel>
            <v-expansion-panel-header class="accordion-header">
              <div class="accordion-title">
                <v-icon size="20" color="red" class="mr-2">mdi-video</v-icon>
                <span>Media Links</span>
                <v-chip small color="red" text-color="white" class="ml-2">
                  {{ externalMediaLinkCount }}
                </v-chip>
              </div>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <div v-if="externalMediaLinks.length === 0" class="empty-state-text">
                <v-icon size="40" color="grey lighten-2">mdi-video-off</v-icon>
                <p>No media links shared yet</p>
              </div>
              <div v-else class="links-list">
                <div v-for="(link, index) in externalMediaLinks" :key="index" class="link-item" @click="openLink(link.url)">
                  <div class="link-icon" :style="{ background: getPlatformColor(link.platform) }">
                    <v-icon size="20" color="white">{{ getPlatformIcon(link.platform) }}</v-icon>
                  </div>
                  <div class="link-details">
                    <div class="link-url">{{ link.name || link.url }}</div>
                    <div class="link-meta">
                      <span class="link-platform">{{ link.platform || 'External' }}</span>
                      <span class="link-sender">{{ link.uploadedByName || 'Unknown' }}</span>
                      <span class="link-time">{{ formatMessageTime(link.uploadedAt) }}</span>
                    </div>
                  </div>
                  <v-btn icon small class="copy-link-btn" @click.stop="copyLink(link.url)">
                    <v-icon size="16">mdi-content-copy</v-icon>
                  </v-btn>
                </div>
              </div>
            </v-expansion-panel-content>
          </v-expansion-panel>

          <!-- Web Links -->
          <v-expansion-panel>
            <v-expansion-panel-header class="accordion-header">
              <div class="accordion-title">
                <v-icon size="20" color="info" class="mr-2">mdi-web</v-icon>
                <span>Web Links</span>
                <v-chip small color="info" text-color="white" class="ml-2">
                  {{ webLinkCount }}
                </v-chip>
              </div>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <div v-if="webLinks.length === 0" class="empty-state-text">
                <v-icon size="40" color="grey lighten-2">mdi-web-off</v-icon>
                <p>No web links shared yet</p>
              </div>
              <div v-else class="links-list">
                <div v-for="(link, index) in webLinks" :key="index" class="link-item" @click="openLink(link.url)">
                  <div class="link-icon" style="background: rgba(33, 150, 243, 0.15);">
                    <v-icon size="20" color="info">mdi-link-variant</v-icon>
                  </div>
                  <div class="link-details">
                    <div class="link-url">{{ link.name || link.url }}</div>
                    <div class="link-meta">
                      <span class="link-sender">{{ link.uploadedByName || 'Unknown' }}</span>
                      <span class="link-time">{{ formatMessageTime(link.uploadedAt) }}</span>
                    </div>
                  </div>
                  <v-btn icon small class="copy-link-btn" @click.stop="copyLink(link.url)">
                    <v-icon size="16">mdi-content-copy</v-icon>
                  </v-btn>
                </div>
              </div>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </div>

      <!-- Admins -->
      <div class="info-section">
        <h3 class="section-title">
          Admins
          <span class="admin-count">({{ admins.length }})</span>
        </h3>
        <div v-for="admin in admins" :key="admin._id" class="admin-item">
          <editable-avatar :image="getUserAvatar(admin)" :size="36" :editable="false" />
          <div class="admin-details">
            <span class="admin-name">{{ getDisplayName(admin) }}</span>
            <span class="admin-email">{{ admin.email || '' }}</span>
          </div>
          <v-chip v-if="isCreator(admin)" small color="primary" text-color="white">
            Creator
          </v-chip>
          <v-chip v-else color="info" small text-color="white">
            Admin
          </v-chip>
        </div>
      </div>

      <!-- Participants -->
      <div class="info-section">
        <div class="participants-header">
          <h3 class="section-title">
            Participants
            <span class="participant-count">({{ activeParticipants.length }})</span>
          </h3>
          <v-btn v-if="isAdminOrCreator" small color="primary" class="add-member-btn" @click="openAddMemberDialog">
            <v-icon left size="16">mdi-account-plus</v-icon>
            Add Member
          </v-btn>
        </div>
        <div class="participants-list">
          <div v-for="participant in activeParticipants" :key="participant._id || participant.userID"
            class="participant-item">
            <editable-avatar :image="getUserAvatar(participant)" :size="36" :editable="false" />
            <div class="participant-details">
              <span class="participant-name">{{ getDisplayName(participant) }}</span>
              <span class="participant-email">{{ participant.email || '' }}</span>
            </div>
            <div class="participant-status">
              <span v-if="participant.isOnline" class="online-dot"></span>
              <span v-else class="offline-dot"></span>
            </div>

            <!-- Action Buttons for Admins/Creator -->
            <div v-if="isAdminOrCreator && !isCurrentUser(participant) && !isCreator(participant)"
              class="participant-actions">

              <!-- Make Admin Button -->
              <v-btn icon small class="make-admin-btn" @click="openMakeAdminDialog(participant)"
                :loading="loadingAction === participant._id && actionType === 'makeAdmin'" title="Make Admin">
                <v-icon size="18" color="primary">mdi-account-plus</v-icon>
              </v-btn>

              <!-- Remove Button -->
              <v-btn icon small class="remove-user-btn" @click="openRemoveParticipantDialog(participant)"
                :loading="loadingAction === participant._id && actionType === 'remove'" title="Remove user">
                <v-icon size="18" color="error">mdi-close</v-icon>
              </v-btn>
            </div>
          </div>
        </div>
      </div>

      <!-- Actions -->
      <div class="actions-section">
        <v-btn block outlined color="error" class="leave-group-btn" @click="openLeaveGroupDialog">
          <v-icon left>mdi-exit-run</v-icon>
          Leave Group
        </v-btn>
      </div>
    </div>

    <!-- Confirmation Dialogs -->
    <!-- Leave Group Dialog -->
    <v-dialog v-model="leaveGroupDialog" max-width="400" persistent>
      <v-card class="rounded-xl">
        <v-card-text class="pt-6 pb-3 text-center">
          <v-icon color="error" size="64" class="mb-2">mdi-exit-run</v-icon>
          <h2 class="mb-2 text-h5 font-weight-bold">Leave Group?</h2>
          <p class="text-body-1 grey--text">
            Are you sure you want to leave "<strong>{{ groupInfo?.name }}</strong>"?
            You won't be able to see messages or participate in this group until you're added back.
          </p>
        </v-card-text>
        <div class="px-4 pb-3">
          <v-btn block large depressed color="error" dark class="mb-3 rounded-pill" @click="confirmLeaveGroup"
            :loading="leaving">
            <v-icon left>mdi-exit-run</v-icon> Leave Group
          </v-btn>
        </div>
        <v-card-actions class="px-0 pt-0 pb-4">
          <v-btn text block large class="rounded-lg text-capitalize" @click="leaveGroupDialog = false">
            Cancel
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Remove Participant Dialog -->
    <v-dialog v-model="removeParticipantDialog" max-width="400" persistent>
      <v-card class="rounded-xl">
        <v-card-text class="pt-6 pb-3 text-center">
          <v-icon color="error" size="64" class="mb-2">mdi-account-remove</v-icon>
          <h2 class="mb-2 text-h5 font-weight-bold">Remove Participant?</h2>
          <p class="text-body-1 grey--text">
            Are you sure you want to remove "<strong>{{ participantToRemove ? getDisplayName(participantToRemove) : ''
            }}</strong>"
            from this group? They will no longer have access to the group.
          </p>
        </v-card-text>
        <div class="px-4 pb-3">
          <v-btn block large depressed color="error" dark class="mb-3 rounded-pill" @click="confirmRemoveParticipant"
            :loading="loadingAction === participantToRemove?._id && actionType === 'remove'">
            <v-icon left>mdi-account-remove</v-icon> Remove
          </v-btn>
        </div>
        <v-card-actions class="px-0 pt-0 pb-4">
          <v-btn text block large class="rounded-lg text-capitalize" @click="removeParticipantDialog = false">
            Cancel
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Make Admin Dialog -->
    <v-dialog v-model="makeAdminDialog" max-width="400" persistent>
      <v-card class="rounded-xl">
        <v-card-text class="pt-6 pb-3 text-center">
          <v-icon color="primary" size="64" class="mb-2">mdi-account-plus</v-icon>
          <h2 class="mb-2 text-h5 font-weight-bold">Make Admin?</h2>
          <p class="text-body-1 grey--text">
            Are you sure you want to make "<strong>{{ adminToMake ? getDisplayName(adminToMake) : '' }}</strong>"
            an admin? They will be able to manage group settings and participants.
          </p>
        </v-card-text>
        <div class="px-4 pb-3">
          <v-btn block large depressed color="primary" dark class="mb-3 rounded-pill" @click="confirmMakeAdmin"
            :loading="loadingAction === adminToMake?._id && actionType === 'makeAdmin'">
            <v-icon left>mdi-account-plus</v-icon> Make Admin
          </v-btn>
        </div>
        <v-card-actions class="px-0 pt-0 pb-4">
          <v-btn text block large class="rounded-lg text-capitalize" @click="makeAdminDialog = false">
            Cancel
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Remove Admin Dialog -->
    <v-dialog v-model="removeAdminDialog" max-width="400" persistent>
      <v-card class="rounded-xl">
        <v-card-text class="pt-6 pb-3 text-center">
          <v-icon color="warning" size="64" class="mb-2">mdi-account-minus</v-icon>
          <h2 class="mb-2 text-h5 font-weight-bold">Remove Admin?</h2>
          <p class="text-body-1 grey--text">
            Are you sure you want to remove "<strong>{{ adminToRemove ? getDisplayName(adminToRemove) : '' }}</strong>"
            from admin role? They will no longer be able to manage group settings.
          </p>
        </v-card-text>
        <div class="px-4 pb-3">
          <v-btn block large depressed color="warning" dark class="mb-3 rounded-pill" @click="confirmRemoveAdmin"
            :loading="loadingAction === adminToRemove?._id && actionType === 'removeAdmin'">
            <v-icon left>mdi-account-minus</v-icon> Remove Admin
          </v-btn>
        </div>
        <v-card-actions class="px-0 pt-0 pb-4">
          <v-btn text block large class="rounded-lg text-capitalize" @click="removeAdminDialog = false">
            Cancel
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Add Member Dialog -->
    <v-dialog v-model="addMemberDialog" max-width="500" persistent>
      <v-card class="rounded-xl">
        <v-card-title class="dialog-header">
          <v-icon left dark>mdi-account-plus</v-icon>
          <span>Add Member</span>
          <v-spacer></v-spacer>
          <v-btn icon dark @click="closeAddMemberDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pt-4">
          <!-- Search Input -->
          <v-text-field v-model="memberSearchKey" label="Search for users..." prepend-inner-icon="mdi-magnify" outlined
            clearable @input="searchForMembers" :loading="searchingMembers" autofocus />

          <!-- Loading State -->
          <div v-if="searchingMembers" class="text-center py-4">
            <v-progress-circular indeterminate color="primary" size="40" />
            <p class="grey--text mt-2">Searching for users...</p>
          </div>

          <!-- Users List -->
          <div v-else-if="memberSearchResults && memberSearchResults.length > 0" class="user-search-results">
            <v-list>
              <v-list-item v-for="user in memberSearchResults" :key="user._id || user.id"
                @click="addMemberToGroup(user)" class="user-search-item"
                :disabled="isMemberAlreadyInGroup(user) || addingMember">
                <v-list-item-avatar>
                  <editable-avatar :editable="false" :image="getUserAvatar(user)" :size="40" />
                </v-list-item-avatar>
                <v-list-item-content>
                  <v-list-item-title>{{ getUserFullName(user) }}</v-list-item-title>
                  <v-list-item-subtitle>
                    {{ getUserType(user) }}
                    <span v-if="isMemberAlreadyInGroup(user)" class="already-in-group">
                      (Already in group)
                    </span>
                  </v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action>
                  <v-btn icon small :color="isMemberAlreadyInGroup(user) ? 'grey' : 'success'"
                    :loading="addingMember && selectedMemberToAdd?._id === user._id"
                    :disabled="isMemberAlreadyInGroup(user)" @click.stop="addMemberToGroup(user)">
                    <v-icon v-if="isMemberAlreadyInGroup(user)">mdi-check</v-icon>
                    <v-icon v-else>mdi-plus</v-icon>
                  </v-btn>
                </v-list-item-action>
              </v-list-item>
            </v-list>
          </div>

          <!-- No Results -->
          <div v-else-if="memberSearchKey && memberSearchKey.length > 2" class="text-center py-4">
            <v-icon size="48" color="grey lighten-2">mdi-account-search</v-icon>
            <p class="grey--text mt-2">No users found for "{{ memberSearchKey }}"</p>
          </div>

          <!-- Initial State -->
          <div v-else class="text-center py-4">
            <v-icon size="48" color="grey lighten-2">mdi-account-plus</v-icon>
            <p class="grey--text mt-2">Search for users to add to the group</p>
          </div>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeAddMemberDialog">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Media Preview Dialog -->
    <v-dialog v-model="mediaPreviewDialog" max-width="800" persistent>
      <v-card class="rounded-xl">
        <v-card-title class="dialog-header">
          <span>Media Preview</span>
          <v-spacer></v-spacer>
          <v-btn icon dark @click="mediaPreviewDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pa-4 text-center">
          <v-img :src="selectedMediaUrl" max-height="600" contain class="media-preview-image" />
        </v-card-text>
        <v-card-actions class="px-4 pb-4">
          <v-btn color="primary" @click="downloadMedia" block>
            <v-icon left>mdi-download</v-icon>
            Download
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar -->
    <v-snackbar v-model="snackbar" :timeout="4000" bottom :color="snackbarColor">
      <v-icon left>{{ snackbarIcon }}</v-icon>
      {{ snackMessage }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar = false">Close</v-btn>
      </template>
    </v-snackbar>
  </v-navigation-drawer>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import EditableAvatar from "@/components/EditableAvatar.vue";
import socket from "@/socket";

export default {
  name: "GroupInfoSidebar",
  components: { EditableAvatar },

  props: {
    value: {
      type: Boolean,
      default: false
    },
    chatId: {
      type: String,
      default: null
    }
  },

  data() {
    return {
      drawer: false,
      loading: false,
      saving: false,
      leaving: false,
      loadingAction: null,
      actionType: null,
      groupChat: null,
      groupInfo: {
        participants: [],
        groupAdmins: [],
        messages: [],
        groupMetaHistory: [],
        // Media and links from API
        mediaFiles: [],
        documents: [],
        externalMediaLinks: [],
        webLinks: [],
        mediaCount: 0,
        mediaFileCount: 0,
        documentCount: 0,
        externalMediaLinkCount: 0,
        webLinkCount: 0,
        latestMedia: null,
        latestDocument: null,
        latestExternalMediaLink: null,
        latestWebLink: null
      },

      // Edit states
      editingName: false,
      editName: '',
      editingDescription: false,
      editDescription: '',
      showAvatarEdit: false,

      // Dialog states
      leaveGroupDialog: false,
      removeParticipantDialog: false,
      makeAdminDialog: false,
      removeAdminDialog: false,
      mediaPreviewDialog: false,

      // Data for dialogs
      participantToRemove: null,
      adminToMake: null,
      adminToRemove: null,
      selectedMediaUrl: '',

      // Snackbar
      snackbar: false,
      snackMessage: "",
      snackbarIcon: "mdi-information",
      snackbarColor: "info",

      // Add Member Dialog
      addMemberDialog: false,
      memberSearchKey: '',
      memberSearchResults: [],
      searchingMembers: false,
      addingMember: false,
      selectedMemberToAdd: null,
      memberSearchTimeout: null,

      // Socket handlers
      adminRemovedHandler: null,
      adminAddedHandler: null,
      groupUpdatedHandler: null,
      memberRemovedHandler: null,
      memberAddedHandler: null,
    };
  },

  computed: {
    ...mapGetters({
      profile: "auth/getProfile"
    }),

    participantCount() {
      if (!this.groupInfo || !this.groupInfo.participants) return 0;
      return this.groupInfo.participants.length || 0;
    },

    activeParticipants() {
      if (!this.groupInfo || !this.groupInfo.participants || !Array.isArray(this.groupInfo.participants)) {
        return [];
      }
      return this.groupInfo.participants.filter(p => p && p.isActive !== false);
    },

    admins() {
      if (!this.groupInfo || !this.groupInfo.groupAdmins || !Array.isArray(this.groupInfo.groupAdmins)) {
        return [];
      }
      return this.groupInfo.groupAdmins;
    },

    // Media Files from API
    mediaFiles() {
      if (this.groupInfo && this.groupInfo.mediaFiles) {
        return this.groupInfo.mediaFiles;
      }
      return [];
    },

    // Documents from API
    documents() {
      if (this.groupInfo && this.groupInfo.documents) {
        return this.groupInfo.documents;
      }
      return [];
    },

    // External Media Links from API
    externalMediaLinks() {
      if (this.groupInfo && this.groupInfo.externalMediaLinks) {
        return this.groupInfo.externalMediaLinks;
      }
      return [];
    },

    // Web Links from API
    webLinks() {
      if (this.groupInfo && this.groupInfo.webLinks) {
        return this.groupInfo.webLinks;
      }
      return [];
    },

    // Counts
    mediaFileCount() {
      if (this.groupInfo) {
        return this.groupInfo.mediaFileCount || 0;
      }
      return this.mediaFiles.length;
    },

    documentCount() {
      if (this.groupInfo) {
        return this.groupInfo.documentCount || 0;
      }
      return this.documents.length;
    },

    externalMediaLinkCount() {
      if (this.groupInfo) {
        return this.groupInfo.externalMediaLinkCount || 0;
      }
      return this.externalMediaLinks.length;
    },

    webLinkCount() {
      if (this.groupInfo) {
        return this.groupInfo.webLinkCount || 0;
      }
      return this.webLinks.length;
    },

    isAdminOrCreator() {
      if (!this.groupInfo || !this.profile) return false;
      const userId = this.profile._id;

      if (this.groupInfo.createdBy?._id === userId) return true;

      if (this.groupInfo.groupAdmins && Array.isArray(this.groupInfo.groupAdmins)) {
        return this.groupInfo.groupAdmins.some(admin => admin && admin._id === userId);
      }
      return false;
    },

    isCreator() {
      return (user) => {
        if (!user || !this.groupInfo?.createdBy) return false;
        return user._id === this.groupInfo.createdBy._id;
      };
    },

    isCurrentUser() {
      return (user) => {
        if (!user || !this.profile) return false;
        return user._id === this.profile._id;
      };
    },

    isAdmin() {
      return (user) => {
        if (!user || !this.groupInfo) return false;
        if (!this.groupInfo.groupAdmins || !Array.isArray(this.groupInfo.groupAdmins)) return false;
        const userId = user._id || user.userID;
        return this.groupInfo.groupAdmins.some(admin => {
          const adminId = admin._id || admin.userID;
          return adminId && adminId.toString() === userId.toString();
        });
      };
    },
  },

  watch: {
    value(val) {
      this.drawer = val;
      if (val && this.chatId) {
        this.loadGroupInfo();
      }
    },
    drawer(val) {
      if (!val) {
        this.$emit('input', false);
      }
    }
  },

  mounted() {
    this.setupSocketListeners();
  },

  beforeDestroy() {
    this.cleanupSocketListeners();
  },

  methods: {
    ...mapActions({
      getGroupChatInfo: "chat/getGroupChatInfo",
      updateGroupChatDetails: "chat/updateGroupChatDetails",
      removeMemberFromGroupChat: "chat/removeMemberFromGroupChat",
      leaveGroupChat: "chat/leaveGroupChat",
      makeGroupAdmin: "chat/makeGroupAdmin",
      removeGroupAdmin: "chat/removeGroupAdmin",
      searchUsers: "chat/searchUsers",
      addMemberToGroupChat: "chat/addMemberToGroupChat"
    }),

    closeDrawer() {
      this.drawer = false;
    },

    setupSocketListeners() {
      this.adminRemovedHandler = (data) => {
        this.handleAdminRemoved(data);
      };
      socket.on('admin_removed', this.adminRemovedHandler);

      this.adminAddedHandler = (data) => {
        this.handleAdminAdded(data);
      };
      socket.on('admin_added', this.adminAddedHandler);

      this.groupUpdatedHandler = (data) => {
        this.handleGroupUpdated(data);
      };
      socket.on('group_updated', this.groupUpdatedHandler);

      this.memberRemovedHandler = (data) => {
        this.handleMemberRemoved(data);
      };
      socket.on('member_removed', this.memberRemovedHandler);

      this.memberAddedHandler = (data) => {
        this.handleMemberAdded(data);
      };
      socket.on('member_added', this.memberAddedHandler);
    },

    cleanupSocketListeners() {
      const handlers = [
        ['admin_removed', this.adminRemovedHandler],
        ['admin_added', this.adminAddedHandler],
        ['group_updated', this.groupUpdatedHandler],
        ['member_removed', this.memberRemovedHandler],
        ['member_added', this.memberAddedHandler],
      ];

      handlers.forEach(([event, handler]) => {
        if (handler) {
          socket.off(event, handler);
        }
      });

      this.adminRemovedHandler = null;
      this.adminAddedHandler = null;
      this.groupUpdatedHandler = null;
      this.memberRemovedHandler = null;
      this.memberAddedHandler = null;
    },

    handleAdminRemoved(data) {
      if (!data || data.chatId !== this.groupInfo?._id) return;

      if (data.removedAdmin) {
        this.groupInfo.groupAdmins = this.groupInfo.groupAdmins.filter(
          admin => admin._id !== data.removedAdmin._id
        );

        const participantIndex = this.groupInfo.participants.findIndex(
          p => (p._id || p.userID) === data.removedAdmin._id
        );
        if (participantIndex !== -1) {
          this.groupInfo.participants[participantIndex].role = 'member';
        }
      }

      if (data.systemMessage && this.groupInfo.messages) {
        this.groupInfo.messages.push({
          ...data.systemMessage,
          messageType: 'system',
          systemMeta: {
            action: 'admin_removed',
            summary: data.systemMessage.text
          },
          countsTowardUnread: false
        });
      }

      this.$forceUpdate();
    },

    handleAdminAdded(data) {
      if (!data || data.chatId !== this.groupInfo?._id) return;

      if (data.newAdmin) {
        const exists = this.groupInfo.groupAdmins.some(
          admin => admin._id === data.newAdmin._id
        );
        if (!exists) {
          this.groupInfo.groupAdmins.push(data.newAdmin);
        }

        const participantIndex = this.groupInfo.participants.findIndex(
          p => (p._id || p.userID) === data.newAdmin._id
        );
        if (participantIndex !== -1) {
          this.groupInfo.participants[participantIndex].role = 'admin';
        }
      }

      if (data.systemMessage && this.groupInfo.messages) {
        this.groupInfo.messages.push({
          ...data.systemMessage,
          messageType: 'system',
          systemMeta: {
            action: 'admin_added',
            summary: data.systemMessage.text
          },
          countsTowardUnread: false
        });
      }

      this.$forceUpdate();
    },

    handleGroupUpdated(data) {
      if (!data || data.chatId !== this.groupInfo?._id) return;

      if (data.updates) {
        if (data.updates.name) {
          this.groupInfo.name = data.updates.name;
        }
        if (data.updates.description !== undefined) {
          this.groupInfo.description = data.updates.description;
        }
        if (data.updates.groupImage) {
          this.groupInfo.groupImage = data.updates.groupImage;
        }
      }

      if (data.systemMessage) {
        if (!this.groupInfo.messages) {
          this.groupInfo.messages = [];
        }

        const exists = this.groupInfo.messages.some(
          m => m._id && m._id === data.systemMessage._id
        );

        if (!exists) {
          this.groupInfo.messages.push({
            ...data.systemMessage,
            messageType: 'system',
            systemMeta: {
              action: data.systemMessage.systemMeta?.action || 'group_updated',
              summary: data.systemMessage.text,
              changes: data.systemMessage.systemMeta?.changes || []
            },
            countsTowardUnread: false,
            _id: data.systemMessage._id || `sys_${Date.now()}`
          });
        }
      }

      this.$forceUpdate();
    },

    handleMemberRemoved(data) {
      const currentChatId = this.chatId || this.groupInfo?._id;

      if (!data || data.chatId !== String(currentChatId || "")) {
        return;
      }

      const memberId = data.removedMember?._id || data.memberId;
      if (memberId && this.groupInfo.participants) {
        this.groupInfo.participants = this.groupInfo.participants.filter(
          p => {
            const pId = p._id || p.userID;
            return pId && pId.toString() !== memberId.toString();
          }
        );

        if (this.groupInfo.groupAdmins) {
          this.groupInfo.groupAdmins = this.groupInfo.groupAdmins.filter(
            admin => {
              const adminId = admin._id || admin.userID;
              return adminId && adminId.toString() !== memberId.toString();
            }
          );
        }
      }

      if (data.systemMessage && this.groupInfo.messages) {
        const exists = this.groupInfo.messages.some(
          m => m._id && m._id.toString() === data.systemMessage._id.toString()
        );

        if (!exists) {
          this.groupInfo.messages.push({
            _id: data.systemMessage._id || `sys_${Date.now()}`,
            text: data.systemMessage.text,
            messageType: 'system',
            systemMeta: {
              action: 'member_removed',
              summary: data.systemMessage.text,
              removedUser: data.removedMember,
              removedBy: data.removedBy
            },
            countsTowardUnread: false,
            createdAt: data.systemMessage.createdAt || new Date(),
            sender: {
              _id: data.removedBy?._id || '',
              userType: data.removedBy?.userType || 'User',
              name: data.removedBy?.name || 'Someone'
            }
          });
        }
      }

      this.$forceUpdate();
    },

    handleMemberAdded(data) {
      const currentChatId = this.chatId || this.groupInfo?._id;

      if (!data || data.chatId !== String(currentChatId || "")) {
        return;
      }

      if (data.newMember && this.groupInfo.participants) {
        const memberId = data.newMember._id || data.newMember.userID;
        const exists = this.groupInfo.participants.some(
          p => {
            const pId = p._id || p.userID;
            return pId && pId.toString() === memberId.toString();
          }
        );

        if (!exists) {
          this.groupInfo.participants.push({
            _id: memberId,
            userType: data.newMember.userType || 'User',
            name: data.newMember.name || 'User',
            email: data.newMember.email || '',
            image: data.newMember.image || null,
            role: 'member',
            joinedAt: data.addedAt || new Date(),
            isActive: true
          });
        }
      }

      if (data.systemMessage && this.groupInfo.messages) {
        const exists = this.groupInfo.messages.some(
          m => m._id && m._id.toString() === data.systemMessage._id.toString()
        );

        if (!exists) {
          this.groupInfo.messages.push({
            _id: data.systemMessage._id || `sys_${Date.now()}`,
            text: data.systemMessage.text,
            messageType: 'system',
            systemMeta: {
              action: 'member_added',
              summary: data.systemMessage.text,
              addedUser: data.newMember,
              addedBy: data.addedBy
            },
            countsTowardUnread: false,
            createdAt: data.systemMessage.createdAt || new Date(),
            sender: {
              _id: data.addedBy?._id || '',
              userType: data.addedBy?.userType || 'User',
              name: data.addedBy?.name || 'Someone'
            }
          });
        }
      }

      this.$forceUpdate();
    },

    async loadGroupInfo() {
      if (!this.chatId) return;

      this.loading = true;
      try {
        const result = await this.getGroupChatInfo(this.chatId);
        if (result) {
          const existingMessages = this.groupInfo?.messages || [];

          const safeResult = {
            ...result,
            participants: Array.isArray(result.participants) ? result.participants : [],
            groupAdmins: Array.isArray(result.groupAdmins) ? result.groupAdmins : [],
            messages: Array.isArray(result.messages) ? result.messages : existingMessages,
            groupMetaHistory: Array.isArray(result.groupMetaHistory) ? result.groupMetaHistory : [],
            removedMembers: Array.isArray(result.removedMembers) ? result.removedMembers : [],
            groupSettings: result.groupSettings || {},
            unreadCount: result.unreadCount || {},
            mediaFiles: Array.isArray(result.mediaFiles) ? result.mediaFiles : [],
            documents: Array.isArray(result.documents) ? result.documents : [],
            externalMediaLinks: Array.isArray(result.externalMediaLinks) ? result.externalMediaLinks : [],
            webLinks: Array.isArray(result.webLinks) ? result.webLinks : [],
            mediaCount: result.mediaCount || 0,
            mediaFileCount: result.mediaFileCount || 0,
            documentCount: result.documentCount || 0,
            externalMediaLinkCount: result.externalMediaLinkCount || 0,
            webLinkCount: result.webLinkCount || 0,
            latestMedia: result.latestMedia || null,
            latestDocument: result.latestDocument || null,
            latestExternalMediaLink: result.latestExternalMediaLink || null,
            latestWebLink: result.latestWebLink || null
          };

          this.groupInfo = safeResult;
          this.groupChat = result;
          this.editName = safeResult.name || '';
          this.editDescription = safeResult.description || '';
          
          this.$forceUpdate();
        }
      } catch (error) {
        console.error("Error loading group info:", error);
        this.showSnackbar("Failed to load group info", "error", "mdi-alert");
      } finally {
        this.loading = false;
      }
    },

    getUserAvatar(user) {
      if (!user) return require("@/assets/default.jpg");
      return user.midThumbnail || user.thumbnail || user.avatar || user.image || require("@/assets/default.jpg");
    },

    getDisplayName(user) {
      if (!user) return "Unknown";
      if (user.name) return user.name;
      if (user.first_name && user.last_name) {
        return `${user.first_name} ${user.last_name}`.trim();
      }
      if (user.first_name) return user.first_name;
      if (user.displayName) return user.displayName;
      return "User";
    },

    formatDate(date) {
      if (!date) return "";
      return new Date(date).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric"
      });
    },

    formatMessageTime(date) {
      if (!date) return "";
      return new Date(date).toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit"
      });
    },

    formatFileSize(bytes) {
      if (!bytes || bytes === 0) return '0 B';
      const k = 1024;
      const sizes = ['B', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    },

getMediaIcon(type) {
  const icons = {
    image: 'mdi-image',
    video: 'mdi-video',
    audio: 'mdi-music',
    document: 'mdi-file-document',
    pdf: 'mdi-file-pdf',
    'file': 'mdi-file'
  };
  return icons[type] || 'mdi-file';
},

    getDocumentIcon(type) {
      const icons = {
        'pdf': 'mdi-file-pdf',
        'doc': 'mdi-file-word',
        'docx': 'mdi-file-word',
        'xls': 'mdi-file-excel',
        'xlsx': 'mdi-file-excel',
        'ppt': 'mdi-file-powerpoint',
        'pptx': 'mdi-file-powerpoint',
        'txt': 'mdi-file-document',
        'zip': 'mdi-file-zip',
        'rar': 'mdi-file-zip',
        'jpg': 'mdi-file-image',
        'jpeg': 'mdi-file-image',
        'png': 'mdi-file-image',
        'gif': 'mdi-file-image'
      };
      return icons[type] || 'mdi-file-document';
    },

    getDocumentColor(type) {
      const colors = {
        'pdf': 'red',
        'doc': 'blue',
        'docx': 'blue',
        'xls': 'green',
        'xlsx': 'green',
        'ppt': 'orange',
        'pptx': 'orange',
        'txt': 'grey',
        'zip': 'brown',
        'rar': 'brown'
      };
      return colors[type] || 'grey';
    },

    getPlatformIcon(platform) {
      const icons = {
        youtube: 'mdi-youtube',
        vimeo: 'mdi-vimeo',
        dailymotion: 'mdi-play',
        twitch: 'mdi-twitch',
        external: 'mdi-video'
      };
      return icons[platform] || 'mdi-video';
    },

    getPlatformColor(platform) {
      const colors = {
        youtube: 'rgba(255, 0, 0, 0.8)',
        vimeo: 'rgba(26, 183, 234, 0.8)',
        dailymotion: 'rgba(0, 100, 210, 0.8)',
        twitch: 'rgba(100, 65, 164, 0.8)',
        external: 'rgba(33, 150, 243, 0.8)'
      };
      return colors[platform] || 'rgba(33, 150, 243, 0.8)';
    },

    // ===== Image Upload =====
    triggerImageUpload() {
      this.$refs.imageInput.click();
    },

    async handleImageUpload(event) {
      const file = event.target.files[0];
      if (!file) return;

      if (file.size > 5 * 1024 * 1024) {
        this.showSnackbar("Image size should be less than 5MB", "warning", "mdi-alert");
        event.target.value = '';
        return;
      }

      try {
        this.saving = true;

        const formData = new FormData();
        formData.append('chatId', this.groupInfo._id);
        formData.append('chatImage', file);

        const result = await this.updateGroupChatDetails(formData);

        if (result && result.success) {
          if (result.data?.currentValues?.image) {
            this.groupInfo.groupImage = result.data.currentValues.image;
          }

          if (result.data?.chat) {
            this.groupInfo = {
              ...this.groupInfo,
              ...result.data.chat,
              messages: result.data.chat.messages || this.groupInfo.messages || []
            };
          }

          this.showSnackbar("Group image updated successfully", "success", "mdi-check");
          this.$emit('group-updated', {
            chatId: this.groupInfo._id,
            updates: { groupImage: this.groupInfo.groupImage }
          });
        } else {
          throw new Error(result?.message || "Failed to update group image");
        }
      } catch (error) {
        console.error("Error uploading image:", error);
        this.showSnackbar(error.message || "Failed to update group image", "error", "mdi-alert");
      } finally {
        this.saving = false;
        event.target.value = '';
      }
    },

    // ===== Edit Name =====
    startEditName() {
      this.editingName = true;
      this.editName = this.groupInfo.name || '';
      this.$nextTick(() => {
        if (this.$refs.nameInput) {
          this.$refs.nameInput.focus();
          this.$refs.nameInput.select();
        }
      });
    },

    cancelEditName() {
      this.editingName = false;
      this.editName = this.groupInfo.name || '';
    },

    async saveName() {
      if (!this.editName.trim()) {
        this.showSnackbar("Group name cannot be empty", "warning", "mdi-alert");
        return;
      }

      this.saving = true;
      try {
        const result = await this.updateGroupChatDetails({
          groupId: this.groupInfo._id,
          updates: {
            name: this.editName.trim(),
            chatName: this.editName.trim()
          }
        });

        if (result && result.success) {
          const newName = result.data?.currentValues?.name || this.editName.trim();
          this.groupInfo.name = newName;

          const existingMessages = this.groupInfo.messages || [];

          if (result.data?.chat) {
            this.groupInfo = {
              ...this.groupInfo,
              ...result.data.chat,
              messages: result.data.chat.messages && result.data.chat.messages.length > 0
                ? result.data.chat.messages
                : existingMessages
            };
          }

          this.editingName = false;
          this.showSnackbar("Group name updated successfully", "success", "mdi-check");

          this.$emit('group-updated', {
            chatId: this.groupInfo._id,
            updates: { name: newName }
          });

          this.$store.commit('chat/UPDATE_CHAT', {
            _id: this.groupInfo._id,
            name: newName,
            messages: this.groupInfo.messages || []
          });

          this.$forceUpdate();
        } else {
          throw new Error(result?.message || "Failed to update name");
        }
      } catch (error) {
        console.error("Error updating group name:", error);
        this.showSnackbar(error.message || "Failed to update group name", "error", "mdi-alert");
      } finally {
        this.saving = false;
      }
    },

    // ===== Edit Description =====
    startEditDescription() {
      this.editingDescription = true;
      this.editDescription = this.groupInfo.description || '';
      this.$nextTick(() => {
        if (this.$refs.descriptionInput) {
          this.$refs.descriptionInput.focus();
          this.$refs.descriptionInput.select();
        }
      });
    },

    cancelEditDescription() {
      this.editingDescription = false;
      this.editDescription = this.groupInfo.description || '';
    },

    async saveDescription() {
      this.saving = true;
      try {
        const result = await this.updateGroupChatDetails({
          groupId: this.groupInfo._id,
          updates: {
            description: this.editDescription.trim(),
            chatDescription: this.editDescription.trim()
          }
        });

        if (result && result.success) {
          if (result.data?.currentValues?.description) {
            this.groupInfo.description = result.data.currentValues.description;
          } else {
            this.groupInfo.description = this.editDescription.trim();
          }

          if (result.data?.chat) {
            this.groupInfo = {
              ...this.groupInfo,
              ...result.data.chat,
              messages: result.data.chat.messages || this.groupInfo.messages || []
            };
          }

          this.editingDescription = false;
          this.showSnackbar("Group description updated successfully", "success", "mdi-check");

          this.$emit('group-updated', {
            chatId: this.groupInfo._id,
            updates: { description: this.groupInfo.description }
          });
        } else {
          throw new Error(result?.message || "Failed to update description");
        }
      } catch (error) {
        console.error("Error updating group description:", error);
        this.showSnackbar(error.message || "Failed to update group description", "error", "mdi-alert");
      } finally {
        this.saving = false;
      }
    },

    // ===== Dialog Methods =====
    openLeaveGroupDialog() {
      this.leaveGroupDialog = true;
    },

    openRemoveParticipantDialog(participant) {
      this.participantToRemove = participant;
      this.removeParticipantDialog = true;
    },

    openMakeAdminDialog(participant) {
      if (this.isAdmin(participant)) {
        this.adminToRemove = participant;
        this.removeAdminDialog = true;
      } else {
        this.adminToMake = participant;
        this.makeAdminDialog = true;
      }
    },

    // ===== Confirm Actions =====
    async confirmLeaveGroup() {
      if (!this.groupInfo) return;

      this.leaving = true;
      try {
        const result = await this.leaveGroupChat({
          groupId: this.groupInfo._id,
          userId: this.profile._id
        });

        if (result && result.success) {
          this.leaveGroupDialog = false;
          this.closeDrawer();
          this.$emit('left-group', this.groupInfo._id);
          this.showSnackbar(`You left "${this.groupInfo.name}"`, "success", "mdi-check");
          this.$router.push({ name: "chat-details", query: {} });
          this.$store.commit('chat/setSingleRoom', null);
          await this.$store.dispatch('chat/getAllChats');
        } else {
          throw new Error(result?.message || "Failed to leave group");
        }
      } catch (error) {
        console.error("Error leaving group:", error);
        this.showSnackbar(
          error.message || "Failed to leave group. Please try again.",
          "error",
          "mdi-alert"
        );
      } finally {
        this.leaving = false;
      }
    },

    async confirmMakeAdmin() {
      if (!this.adminToMake) {
        this.showSnackbar('No user selected', 'warning', 'mdi-alert');
        return;
      }

      this.loadingAction = this.adminToMake._id || this.adminToMake.userID;
      this.actionType = 'makeAdmin';

      try {
        const userId = this.adminToMake._id || this.adminToMake.userID;
        const userType = this.adminToMake.userType || this.adminToMake.type || 'User';

        if (!userId) {
          throw new Error('Invalid user ID');
        }

        const result = await this.makeGroupAdmin({
          groupId: this.groupInfo._id,
          userId: userId,
          userType: userType
        });

        if (result && result.success) {
          if (!this.groupInfo.groupAdmins) {
            this.groupInfo.groupAdmins = [];
          }

          const exists = this.groupInfo.groupAdmins.some(
            admin => admin._id === userId
          );

          if (!exists) {
            const adminData = {
              _id: userId,
              userType: userType,
              name: this.getDisplayName(this.adminToMake),
              email: this.adminToMake.email || '',
              image: this.adminToMake.image || this.adminToMake.midThumbnail || null,
              role: 'admin',
              addedAt: new Date()
            };

            this.groupInfo.groupAdmins.push(adminData);
          }

          const participantIndex = this.groupInfo.participants.findIndex(
            p => (p._id || p.userID) === userId
          );
          if (participantIndex !== -1) {
            this.groupInfo.participants[participantIndex].role = 'admin';
          }

          this.makeAdminDialog = false;
          const adminName = this.getDisplayName(this.adminToMake);
          this.adminToMake = null;
          this.showSnackbar(`${adminName} is now an admin`, "success", "mdi-check");

          this.$forceUpdate();
          await this.loadGroupInfo();
        } else {
          throw new Error(result?.message || 'Failed to make admin');
        }
      } catch (error) {
        console.error('Error making admin:', error);
        this.showSnackbar(
          error.message || 'Failed to make admin. Please try again.',
          'error',
          'mdi-alert'
        );
      } finally {
        this.loadingAction = null;
        this.actionType = null;
      }
    },

    async confirmRemoveAdmin() {
      if (!this.adminToRemove) {
        this.showSnackbar('No user selected', 'warning', 'mdi-alert');
        return;
      }

      this.loadingAction = this.adminToRemove._id || this.adminToRemove.userID;
      this.actionType = 'removeAdmin';

      try {
        const userId = this.adminToRemove._id || this.adminToRemove.userID;

        if (!userId) {
          throw new Error('Invalid user ID');
        }

        const result = await this.removeGroupAdmin({
          groupId: this.groupInfo._id,
          userId: userId
        });

        if (result && result.success) {
          this.groupInfo.groupAdmins = this.groupInfo.groupAdmins.filter(
            admin => {
              const adminId = admin._id || admin.userID;
              return adminId && adminId.toString() !== userId.toString();
            }
          );

          const participantIndex = this.groupInfo.participants.findIndex(
            p => (p._id || p.userID) === userId
          );
          if (participantIndex !== -1) {
            this.groupInfo.participants[participantIndex].role = 'member';
          }

          this.removeAdminDialog = false;
          const adminName = this.getDisplayName(this.adminToRemove);
          this.adminToRemove = null;
          this.showSnackbar(`${adminName} is no longer an admin`, "success", "mdi-check");

          this.$forceUpdate();
          await this.loadGroupInfo();
        } else {
          throw new Error(result?.message || 'Failed to remove admin');
        }
      } catch (error) {
        console.error('Error removing admin:', error);
        this.showSnackbar(
          error.message || 'Failed to remove admin. Please try again.',
          'error',
          'mdi-alert'
        );
      } finally {
        this.loadingAction = null;
        this.actionType = null;
      }
    },

    async confirmRemoveParticipant() {
      if (!this.participantToRemove) {
        this.showSnackbar('No participant selected', 'warning', 'mdi-alert');
        return;
      }

      this.loadingAction = this.participantToRemove._id || this.participantToRemove.userID;
      this.actionType = 'remove';

      try {
        const memberId = this.participantToRemove._id || this.participantToRemove.userID;

        if (!memberId) {
          throw new Error('Invalid member ID');
        }

        const result = await this.removeMemberFromGroupChat({
          groupId: this.groupInfo._id,
          memberId: memberId
        });

        if (result && result.success) {
          const participantName = this.getDisplayName(this.participantToRemove);

          this.groupInfo.participants = this.groupInfo.participants.filter(
            p => {
              const pId = p._id || p.userID;
              return pId && pId.toString() !== memberId.toString();
            }
          );

          this.groupInfo.groupAdmins = this.groupInfo.groupAdmins.filter(
            admin => {
              const adminId = admin._id || admin.userID;
              return adminId && adminId.toString() !== memberId.toString();
            }
          );

          this.removeParticipantDialog = false;
          this.participantToRemove = null;
          this.showSnackbar(`${participantName} removed from the group`, "success", "mdi-check");

          this.$forceUpdate();

          if (result.systemMessage) {
            const exists = this.groupInfo.messages.some(
              m => m._id && m._id === result.systemMessage._id
            );
            if (!exists) {
              this.groupInfo.messages.push({
                _id: result.systemMessage._id || `sys_${Date.now()}`,
                text: result.systemMessage.text || `${this.profile.name} removed ${participantName} from the group`,
                messageType: 'system',
                systemMeta: {
                  action: 'member_removed',
                  summary: result.systemMessage.text,
                  removedUser: this.participantToRemove,
                  removedBy: {
                    _id: this.profile._id,
                    userType: this.profile.type || 'User',
                    name: this.profile.name || 'You'
                  }
                },
                countsTowardUnread: false,
                createdAt: result.systemMessage.createdAt || new Date()
              });
            }
          }

          await this.loadGroupInfo();
        } else {
          throw new Error(result?.message || "Failed to remove participant");
        }
      } catch (error) {
        console.error("Error removing participant:", error);
        const errorMessage = error.response?.data?.message || error.message || "Failed to remove participant";
        this.showSnackbar(errorMessage, "error", "mdi-alert");
      } finally {
        this.loadingAction = null;
        this.actionType = null;
      }
    },

    // ===== Add Member =====
    openAddMemberDialog() {
      this.memberSearchKey = '';
      this.memberSearchResults = [];
      this.selectedMemberToAdd = null;
      this.addMemberDialog = true;

      this.$nextTick(() => {
        this.searchForMembers();
      });
    },

    closeAddMemberDialog() {
      this.addMemberDialog = false;
      this.memberSearchKey = '';
      this.memberSearchResults = [];
      this.selectedMemberToAdd = null;
      if (this.memberSearchTimeout) {
        clearTimeout(this.memberSearchTimeout);
        this.memberSearchTimeout = null;
      }
    },

    async searchForMembers() {
      if (this.memberSearchTimeout) {
        clearTimeout(this.memberSearchTimeout);
      }

      if (!this.memberSearchKey || this.memberSearchKey.trim() === '') {
        this.memberSearchTimeout = setTimeout(async () => {
          try {
            this.searchingMembers = true;
            const response = await this.searchUsers({ search: '' });
            const users = this.processSearchResults(response);
            this.memberSearchResults = this.filterExistingMembers(users);
          } catch (error) {
            console.error('Error loading users:', error);
            this.memberSearchResults = [];
            this.showSnackbar('Failed to load users', 'warning', 'mdi-alert');
          } finally {
            this.searchingMembers = false;
          }
        }, 300);
        return;
      }

      if (this.memberSearchKey.length < 2) {
        return;
      }

      this.memberSearchTimeout = setTimeout(async () => {
        try {
          this.searchingMembers = true;
          const response = await this.searchUsers({
            search: this.memberSearchKey.trim()
          });
          const users = this.processSearchResults(response);
          this.memberSearchResults = this.filterExistingMembers(users);
        } catch (error) {
          console.error('Error searching users:', error);
          this.memberSearchResults = [];
          this.showSnackbar('Failed to search users', 'warning', 'mdi-alert');
        } finally {
          this.searchingMembers = false;
        }
      }, 500);
    },

    processSearchResults(response) {
      let users = [];
      if (Array.isArray(response)) {
        users = response;
      } else if (response?.res && Array.isArray(response.res)) {
        users = response.res;
      } else if (response?.data && Array.isArray(response.data)) {
        users = response.data;
      } else {
        users = [];
      }
      return users;
    },

    filterExistingMembers(users) {
      if (!users || !Array.isArray(users)) return [];

      const existingMemberIds = new Set();

      if (this.groupInfo && this.groupInfo.participants && Array.isArray(this.groupInfo.participants)) {
        this.groupInfo.participants.forEach(p => {
          if (p) {
            const id = p._id || p.userID;
            if (id) existingMemberIds.add(id.toString());
          }
        });
      }

      if (this.groupInfo?.createdBy?._id) {
        existingMemberIds.add(this.groupInfo.createdBy._id.toString());
      }

      return users.filter(user => {
        if (!user) return false;
        const userId = user._id || user.id;
        return userId && !existingMemberIds.has(userId.toString());
      });
    },

    isMemberAlreadyInGroup(user) {
      if (!user || !this.groupInfo) return false;
      const userId = user._id || user.id;
      if (!userId) return false;

      if (this.groupInfo.participants && Array.isArray(this.groupInfo.participants)) {
        const exists = this.groupInfo.participants.some(p => {
          if (!p) return false;
          const pId = p._id || p.userID;
          return pId && pId.toString() === userId.toString();
        });
        if (exists) return true;
      }

      if (this.groupInfo.createdBy?._id) {
        return this.groupInfo.createdBy._id.toString() === userId.toString();
      }

      return false;
    },

    getUserFullName(user) {
      if (!user) return "Unknown User";
      if (user.first_name && user.last_name) {
        return `${user.first_name} ${user.last_name}`.trim();
      }
      if (user.first_name) return user.first_name;
      if (user.name) return user.name;
      if (user.displayName) return user.displayName;
      if (user.companyName) return user.companyName;
      if (user.email) return user.email.split('@')[0];
      return "User";
    },

    getUserType(user) {
      if (!user) return 'User';
      if (user.userType) return user.userType;
      if (user.type) return user.type;
      if (user.role) return user.role;
      if (user.companyName) return 'Agency';
      return 'User';
    },

    async addMemberToGroup(user) {
      if (!user) return;

      if (this.isMemberAlreadyInGroup(user)) {
        this.showSnackbar('User is already in the group', 'warning', 'mdi-alert');
        return;
      }

      this.selectedMemberToAdd = user;
      this.addingMember = true;

      try {
        const userId = user._id || user.id;
        const userType = user.userType || user.type || 'User';

        if (!userId) {
          throw new Error('User ID is missing');
        }

        const payload = {
          groupId: this.groupInfo._id,
          newMemberId: userId,
          newMemberType: userType
        };

        const result = await this.addMemberToGroupChat(payload);

        if (result && result.success) {
          const exists = this.groupInfo.participants.some(
            p => (p._id || p.userID) === userId
          );

          if (!exists) {
            const newMember = {
              _id: userId,
              userType: userType,
              name: this.getUserFullName(user),
              email: user.email || '',
              image: user.midThumbnail || user.avatar || user.image || null,
              role: 'member',
              joinedAt: new Date(),
              isActive: true
            };

            this.groupInfo.participants.push(newMember);
          }

          this.memberSearchResults = this.memberSearchResults.filter(
            u => (u._id || u.id) !== userId
          );

          this.showSnackbar(
            `${this.getUserFullName(user)} added to the group`,
            'success',
            'mdi-check'
          );

          this.memberSearchKey = '';
          await this.searchForMembers();

          this.$forceUpdate();

          if (result.systemMessage) {
            const exists = this.groupInfo.messages.some(
              m => m._id && m._id === result.systemMessage._id
            );
            if (!exists) {
              this.groupInfo.messages.push({
                _id: result.systemMessage._id || `sys_${Date.now()}`,
                text: result.systemMessage.text || `${this.profile.name} added ${this.getUserFullName(user)} to the group`,
                messageType: 'system',
                systemMeta: {
                  action: 'member_added',
                  summary: result.systemMessage.text,
                  addedUser: {
                    _id: userId,
                    userType: userType,
                    name: this.getUserFullName(user)
                  },
                  addedBy: {
                    _id: this.profile._id,
                    userType: this.profile.type || 'User',
                    name: this.profile.name || 'You'
                  }
                },
                countsTowardUnread: false,
                createdAt: result.systemMessage.createdAt || new Date()
              });
            }
          }
        } else {
          throw new Error(result?.message || 'Failed to add member');
        }
      } catch (error) {
        console.error('Error adding member:', error);
        const errorMessage = error.response?.data?.message || error.message || 'Failed to add member to group';
        this.showSnackbar(errorMessage, 'error', 'mdi-alert');
      } finally {
        this.addingMember = false;
        this.selectedMemberToAdd = null;
      }
    },

    // ===== Media Methods =====
    openMediaPreview(media) {
      this.selectedMediaUrl = media.url;
      this.mediaPreviewDialog = true;
    },

    downloadMedia() {
      const link = document.createElement('a');
      link.href = this.selectedMediaUrl;
      link.download = `media-${Date.now()}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },

    downloadDocument(doc) {
      if (doc.url) {
        const link = document.createElement('a');
        link.href = doc.url;
        link.download = doc.name || 'document';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    },

    // ===== Links Methods =====
    openLink(url) {
      window.open(url, '_blank');
    },

    copyLink(url) {
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(url)
          .then(() => this.showSnackbar("Link copied to clipboard", "success", "mdi-content-copy"))
          .catch(() => this.fallbackCopyLink(url));
      } else {
        this.fallbackCopyLink(url);
      }
    },

    fallbackCopyLink(text) {
      const textArea = document.createElement("textarea");
      textArea.value = text;
      textArea.style.position = "fixed";
      textArea.style.left = "-999999px";
      textArea.style.top = "-999999px";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        if (document.execCommand("copy")) {
          this.showSnackbar("Link copied to clipboard", "success", "mdi-content-copy");
        } else {
          throw new Error("Copy failed");
        }
      } catch (err) {
        this.showSnackbar("Failed to copy link", "error", "mdi-alert");
      } finally {
        document.body.removeChild(textArea);
      }
    },

    // ===== Snackbar =====
    showSnackbar(message, color = "info", icon = "mdi-information") {
      this.snackMessage = message;
      this.snackbarColor = color;
      this.snackbarIcon = icon;
      this.snackbar = true;
    }
  }
};
</script>

<style scoped>
.group-info-sidebar {
  background: #ffffff;
  position: fixed !important;
  top: 0 !important;
  right: 0 !important;
  height: 100vh !important;
  z-index: 1000 !important;
  box-shadow: -4px 0 20px rgba(0, 0, 0, 0.1);
}

.theme--dark .group-info-sidebar {
  background: #111b21;
  box-shadow: -4px 0 20px rgba(0, 0, 0, 0.3);
}

.drawer-header {
  display: flex;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #e4e6ea;
  background: linear-gradient(135deg, #084468, #3b7ca1);
  color: white;
  min-height: 64px;
}

.theme--dark .drawer-header {
  border-bottom-color: rgba(255, 255, 255, 0.08);
  background: linear-gradient(135deg, #0a1a2b, #1a3a5a);
}

.close-btn {
  color: white !important;
  margin-right: 12px;
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.1) !important;
}

.drawer-title {
  font-size: 18px;
  font-weight: 600;
}

.drawer-content {
  padding: 20px;
  overflow-y: auto;
  height: calc(100vh - 64px);
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  gap: 16px;
}

.loading-text {
  color: #8696a0;
  font-size: 14px;
}

/* Group Header */
.group-header {
  text-align: center;
  padding-bottom: 20px;
  border-bottom: 1px solid #e4e6ea;
  margin-bottom: 20px;
}

.theme--dark .group-header {
  border-bottom-color: rgba(255, 255, 255, 0.08);
}

.group-avatar-wrapper {
  margin-bottom: 12px;
  display: flex;
  justify-content: center;
}

.avatar-container {
  position: relative;
  display: inline-block;
}

.group-avatar-large {
  border: 3px solid #084468;
  border-radius: 50%;
}

.theme--dark .group-avatar-large {
  border-color: #3b7ca1;
}

.avatar-edit-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  border-radius: 50%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: opacity 0.3s ease;
  gap: 4px;
}

.edit-text {
  color: white;
  font-size: 11px;
  font-weight: 500;
}

/* Group Name & Description */
.group-name-wrapper,
.group-description-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 6px;
}

.name-display,
.description-display {
  display: flex;
  align-items: center;
  gap: 8px;
}

.group-name {
  font-size: 20px;
  font-weight: 600;
  color: #111b21;
  margin: 0;
}

.theme--dark .group-name {
  color: #e9edef;
}

.group-description {
  font-size: 14px;
  color: #667781;
  margin: 0;
  padding: 4px 0;
}

.theme--dark .group-description {
  color: #8696a0;
}

.edit-btn {
  color: #667781 !important;
}

.edit-btn:hover {
  background: rgba(0, 0, 0, 0.05) !important;
}

.theme--dark .edit-btn {
  color: #8696a0 !important;
}

.theme--dark .edit-btn:hover {
  background: rgba(255, 255, 255, 0.05) !important;
}

.name-edit,
.description-edit {
  width: 100%;
  max-width: 300px;
}

.edit-name-input,
.edit-description-input {
  width: 100%;
}

.edit-name-input>>>.v-input__slot,
.edit-description-input>>>.v-input__slot {
  background: #f5f7fa !important;
}

.theme--dark .edit-name-input>>>.v-input__slot,
.theme--dark .edit-description-input>>>.v-input__slot {
  background: #1a2630 !important;
}

.edit-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 4px;
}

.group-stats {
  display: flex;
  justify-content: center;
  gap: 16px;
  font-size: 13px;
  color: #667781;
  flex-wrap: wrap;
  margin-top: 8px;
}

.theme--dark .group-stats {
  color: #8696a0;
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 4px;
}

/* Info Sections */
.info-section {
  margin-bottom: 24px;
}

.section-title {
  font-size: 14px;
  font-weight: 600;
  color: #667781;
  margin: 0 0 12px 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.theme--dark .section-title {
  color: #8696a0;
}

.admin-count,
.participant-count {
  font-weight: 400;
  font-size: 12px;
}

/* Creator Info */
.creator-info {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 12px;
  background: #f5f7fa;
  border-radius: 8px;
}

.theme--dark .creator-info {
  background: #1a2630;
}

.creator-details {
  flex: 1;
  min-width: 0;
}

.creator-name {
  font-weight: 500;
  color: #111b21;
}

.theme--dark .creator-name {
  color: #e9edef;
}

.creator-email {
  font-size: 12px;
  color: #8696a0;
  display: block;
  word-break: break-all;
}

/* Admin Items */
.admin-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 12px;
  border-radius: 8px;
  margin-bottom: 4px;
}

.admin-item:hover {
  background: #f5f7fa;
}

.theme--dark .admin-item:hover {
  background: #1a2630;
}

.admin-details {
  flex: 1;
  min-width: 0;
}

.admin-name {
  font-weight: 500;
  color: #111b21;
}

.theme--dark .admin-name {
  color: #e9edef;
}

.admin-email {
  font-size: 12px;
  color: #8696a0;
  display: block;
  word-break: break-all;
}

/* Participants List */
.participants-list {
  max-height: 300px;
  overflow-y: auto;
}

.participants-list::-webkit-scrollbar {
  width: 4px;
}

.participants-list::-webkit-scrollbar-track {
  background: transparent;
}

.participants-list::-webkit-scrollbar-thumb {
  background: #c1c7cd;
  border-radius: 4px;
}

.theme--dark .participants-list::-webkit-scrollbar-thumb {
  background: #3a4a5a;
}

.participant-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 8px;
  position: relative;
}

.participant-item:hover {
  background: #f5f7fa;
}

.theme--dark .participant-item:hover {
  background: #1a2630;
}

.participant-details {
  flex: 1;
  min-width: 0;
}

.participant-name {
  font-weight: 500;
  color: #111b21;
}

.theme--dark .participant-name {
  color: #e9edef;
}

.participant-email {
  font-size: 12px;
  color: #8696a0;
  display: block;
  word-break: break-all;
}

.participant-status {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.online-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #31a24c;
  border: 2px solid #ffffff;
}

.offline-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #c1c7cd;
  border: 2px solid #ffffff;
}

.theme--dark .online-dot,
.theme--dark .offline-dot {
  border-color: #111b21;
}

.participant-actions {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}

.make-admin-btn {
  opacity: 0.4;
  transition: opacity 0.2s;
}

.remove-user-btn {
  opacity: 0.4;
  transition: opacity 0.2s;
}

.participant-item:hover .make-admin-btn,
.participant-item:hover .remove-user-btn {
  opacity: 1;
}

.make-admin-btn:hover {
  background: rgba(25, 118, 210, 0.1) !important;
  opacity: 1 !important;
}

.remove-user-btn:hover {
  background: rgba(244, 67, 54, 0.1) !important;
  opacity: 1 !important;
}

/* Actions */
.actions-section {
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid #e4e6ea;
}

.theme--dark .actions-section {
  border-top-color: rgba(255, 255, 255, 0.08);
}

.leave-group-btn {
  border-radius: 8px !important;
  text-transform: none !important;
  font-weight: 500 !important;
}

.leave-group-btn:hover {
  background: rgba(244, 67, 54, 0.04) !important;
}

/* Accordion Styles */
.accordion-header {
  padding: 8px 0 !important;
}

.accordion-title {
  display: flex;
  align-items: center;
  font-weight: 500;
  font-size: 14px;
}

/* Media Grid */
.media-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  padding: 4px 0;
}

.media-item {
  position: relative;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  aspect-ratio: 1;
}

.media-item:hover .media-overlay {
  opacity: 1;
}

.media-thumbnail {
  width: 100%;
  height: 100%;
}

.media-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
  background: #f5f7fa;
}

.media-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.media-type-badge {
  position: absolute;
  top: 8px;
  right: 8px;
  background: rgba(0, 0, 0, 0.6);
  border-radius: 50%;
  padding: 4px;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Documents List */
.documents-list {
  padding: 4px 0;
}

.document-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.2s ease;
  margin-bottom: 4px;
}

.document-item:hover {
  background: #f5f7fa;
}

.theme--dark .document-item:hover {
  background: #1a2630;
}

.document-icon {
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.04);
  display: flex;
  align-items: center;
  justify-content: center;
}

.theme--dark .document-icon {
  background: rgba(255, 255, 255, 0.04);
}

.document-details {
  flex: 1;
  min-width: 0;
}

.document-name {
  font-size: 13px;
  font-weight: 500;
  color: #111b21;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.theme--dark .document-name {
  color: #e9edef;
}

.document-meta {
  display: flex;
  gap: 8px;
  font-size: 11px;
  color: #8696a0;
  margin-top: 2px;
  flex-wrap: wrap;
}

.download-doc-btn {
  opacity: 0.4;
  transition: opacity 0.2s;
}

.document-item:hover .download-doc-btn {
  opacity: 1;
}

.download-doc-btn:hover {
  background: rgba(25, 118, 210, 0.1) !important;
}

/* Links List */
.links-list {
  padding: 4px 0;
}

.link-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.2s ease;
  margin-bottom: 4px;
}

.link-item:hover {
  background: #f5f7fa;
}

.theme--dark .link-item:hover {
  background: #1a2630;
}

.link-icon {
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.link-details {
  flex: 1;
  min-width: 0;
}

.link-url {
  font-size: 13px;
  color: #1976d2;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.theme--dark .link-url {
  color: #42a5f5;
}

.link-meta {
  display: flex;
  gap: 8px;
  font-size: 11px;
  color: #8696a0;
  margin-top: 2px;
  flex-wrap: wrap;
}

.link-platform {
  font-weight: 500;
  text-transform: capitalize;
}

.copy-link-btn {
  opacity: 0.4;
  transition: opacity 0.2s;
}

.link-item:hover .copy-link-btn {
  opacity: 1;
}

.copy-link-btn:hover {
  background: rgba(0, 0, 0, 0.05) !important;
}

/* Empty State */
.empty-state-text {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px 0;
  color: #8696a0;
}

.empty-state-text p {
  margin: 8px 0 0 0;
  font-size: 14px;
}

/* Dialog Header */
.dialog-header {
  background: linear-gradient(135deg, #084468, #3b7ca1);
  color: white;
}

/* Media Preview */
.media-preview-image {
  border-radius: 8px;
}

/* Dark mode overrides */
.theme--dark .empty-state-text {
  color: #8696a0;
}

.theme--dark .media-placeholder {
  background: #1a2630;
}

.theme--dark .link-icon {
  background: rgba(33, 150, 243, 0.15);
}

/* Participants Header */
.participants-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.participants-header .section-title {
  margin-bottom: 0;
}

.add-member-btn {
  text-transform: none !important;
  border-radius: 20px !important;
}

/* Already in group indicator */
.already-in-group {
  color: #999;
  font-size: 12px;
  font-style: italic;
  margin-left: 4px;
}

/* User search results */
.user-search-results {
  max-height: 300px;
  overflow-y: auto;
  margin-top: 8px;
}

.user-search-results::-webkit-scrollbar {
  width: 4px;
}

.user-search-results::-webkit-scrollbar-track {
  background: transparent;
}

.user-search-results::-webkit-scrollbar-thumb {
  background: #c1c7cd;
  border-radius: 4px;
}

.user-search-item {
  cursor: pointer;
  border-radius: 8px;
  margin: 2px 0;
}

.user-search-item:hover {
  background: #f0f2f5;
}

.user-search-item .v-list-item__action {
  min-width: 36px;
}

/* Responsive */
@media (max-width: 600px) {
  .group-info-sidebar {
    width: 100% !important;
    max-width: 100% !important;
  }

  .group-name {
    font-size: 18px;
  }

  .name-edit,
  .description-edit {
    max-width: 100%;
  }

  .make-admin-btn,
  .remove-user-btn {
    opacity: 1;
  }

  .participant-item {
    padding: 6px 8px;
    flex-wrap: wrap;
  }

  .participant-actions {
    margin-left: auto;
  }

  .media-grid {
    grid-template-columns: repeat(3, 1fr);
    gap: 4px;
  }

  .link-item {
    padding: 8px 10px;
  }

  .link-url {
    font-size: 12px;
  }

  .document-item {
    padding: 8px 10px;
  }
}
</style>